// src/admin/components/OrganizationSettingsDialog.tsx
import { Organization } from '@/types/organization';
import { apiFetch } from '@/util/apiClient';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface OrganizationSettingsDialogProps {
  organization: Organization | null;
  onClose: () => void;
}

export function OrganizationSettingsDialog({
  organization,
  onClose,
}: OrganizationSettingsDialogProps) {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState({
    allowSelfRegistration: false,
    requireAdminApproval: true,
    maxDepartments: 10,
    defaultUserRole: 'member',
  });

  useEffect(() => {
    if (organization?.settings) {
      setSettings(organization.settings);
    }
  }, [organization]);

  const updateSettingsMutation = useMutation({
    mutationFn: async () => {
      if (!organization) return;
      await apiFetch(`/api/organizations/${organization.id}/settings/`, {
        method: 'PATCH',
        body: JSON.stringify(settings),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onClose();
    },
  });

  const handleSave = () => {
    updateSettingsMutation.mutate();
  };

  if (!organization) return null;

  return (
    <Dialog open={!!organization} onClose={onClose} maxWidth={'sm'} fullWidth>
      <DialogTitle>Organization Settings - {organization.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.allowSelfRegistration}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    allowSelfRegistration: e.target.checked,
                  })
                }
              />
            }
            label={'Allow users to self-register'}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.requireAdminApproval}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    requireAdminApproval: e.target.checked,
                  })
                }
              />
            }
            label={'Require admin approval for new users'}
          />
          <TextField
            label={'Maximum Departments'}
            type={'number'}
            value={settings.maxDepartments}
            onChange={(e) =>
              setSettings({
                ...settings,
                maxDepartments: parseInt(e.target.value) || 0,
              })
            }
            fullWidth
          />
          <TextField
            label={'Default User Role'}
            value={settings.defaultUserRole}
            onChange={(e) =>
              setSettings({
                ...settings,
                defaultUserRole: e.target.value,
              })
            }
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant={'contained'}
          disabled={updateSettingsMutation.isPending}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
